import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import type { StudentMusicStyleDistribution } from '../../services/adminAnalyticsApi';

const FILL = '#7b1fa2';
const FILL_SELECTED = '#4a148c';

type RankedStyle = StudentMusicStyleDistribution & {
  opacity: number;
};

function rankStyles(data: StudentMusicStyleDistribution[]): RankedStyle[] {
  const sorted = [...data].sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  const last = Math.max(sorted.length - 1, 1);
  return sorted.map((row, index) => ({
    ...row,
    opacity: 0.82 - (index / last) * 0.7,
  }));
}

type Props = {
  data: StudentMusicStyleDistribution[];
  selectedStyleTags: string[];
  onStyleTagClick: (tag: string) => void;
};

const StylePopularityCircles: React.FC<Props> = ({
  data,
  selectedStyleTags,
  onStyleTagClick,
}) => {
  const rows = useMemo(() => rankStyles(data), [data]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.25,
      }}
    >
      {rows.map((row) => {
        const selected = selectedStyleTags.includes(row.tag);
        return (
          <Box
            key={row.tag}
            component="button"
            type="button"
            onClick={() => onStyleTagClick(row.tag)}
            aria-pressed={selected}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              border: 0,
              bgcolor: selected ? 'action.selected' : 'transparent',
              borderRadius: 1,
              px: 0.75,
              py: 0.5,
              cursor: 'pointer',
              textAlign: 'left',
              font: 'inherit',
              color: 'inherit',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                flexShrink: 0,
                bgcolor: selected ? FILL_SELECTED : FILL,
                opacity: selected ? Math.min(1, row.opacity + 0.18) : row.opacity,
                boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.7)',
              }}
            />
            <Typography variant="body2" sx={{ flex: 1, lineHeight: 1.3 }}>
              {row.label}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {row.count}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

export default StylePopularityCircles;
